import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../components/Button/Button';
import { RevealableInput } from '../components/RevealableInput/RevealableInput';
import { useFocusTrap } from '../components/useFocusTrap';
import {
  useRequestMagicLink,
  useRequestPasswordReset,
  useSignin,
  useSignup,
} from './useAuth';
import './AuthDialog.css';

/**
 * Sign-in / sign-up dialog. Two-tab toggle ("New here" vs. "Sign me
 * in") plus a magic-link alternative for kids who don't want to
 * juggle a password yet.
 *
 * Local-first remains the default — nothing in the studio requires an
 * account. This dialog only opens when the kid clicks the "Sign in"
 * affordance in the header.
 */

export type AuthMode = 'signin' | 'signup';

export interface AuthDialogProps {
  isOpen: boolean;
  /** Kicks the dialog open in a particular mode. The kid can swap
   *  modes once it's open. */
  initialMode?: AuthMode;
  onClose: () => void;
}

export function AuthDialog({
  isOpen,
  initialMode = 'signin',
  onClose,
}: AuthDialogProps): JSX.Element | null {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  // Forgot-password substate. Folded into AuthDialog so the kid never
  // navigates away — they pop into the forgot view, request the link,
  // then come back to signin if they remembered after all.
  const [forgotMode, setForgotMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetDevLink, setResetDevLink] = useState<string | null>(null);

  const signin = useSignin();
  const signup = useSignup();
  const requestMagicLink = useRequestMagicLink();
  const requestReset = useRequestPasswordReset();

  useEffect(() => {
    if (!isOpen) {
      // Reset on close so the next open is fresh.
      setEmail('');
      setPassword('');
      setBirthYear('');
      setMagicLinkSent(false);
      setForgotMode(false);
      setResetSent(false);
      setResetDevLink(null);
      signin.reset();
      signup.reset();
      requestMagicLink.reset();
      requestReset.reset();
      return;
    }
    // Sync `mode` to `initialMode` whenever the dialog opens — the
    // useState initializer only runs once, so a second open with a
    // different initialMode (e.g. switching from "Sign in" to "New
    // here" via two separate clicks) needs an explicit reset.
    setMode(initialMode);
    // useFocusTrap below sends focus into the dialog (the email
    // field is marked data-autofocus). We still need an explicit
    // Escape handler since the trap doesn't close the dialog.
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialMode]);

  useFocusTrap(isOpen, dialogRef);

  if (!isOpen) return null;

  const trimmedEmail = email.trim();
  const passwordValid = password.length >= 8;
  const submissionPending =
    signin.isPending || signup.isPending || requestMagicLink.isPending || requestReset.isPending;

  // Inline birth-year validation. Backend enforces the same gate, but
  // we want kids to see a friendly message *before* they hit submit so
  // they don't type a password just to be turned away. Only runs when
  // the kid has typed enough digits to imply a year (≥ 4); we don't
  // want to flash "you're 2024 years old" while they're still typing.
  const birthYearTrim = birthYear.trim();
  type BirthYearProblem = 'too-young' | 'future' | 'unrealistic';
  let birthYearProblem: BirthYearProblem | null = null;
  if (mode === 'signup' && birthYearTrim.length >= 4) {
    const parsed = Number.parseInt(birthYearTrim, 10);
    if (Number.isFinite(parsed)) {
      const currentYear = new Date().getFullYear();
      const age = currentYear - parsed;
      if (age < 0) birthYearProblem = 'future';
      else if (age < 13) birthYearProblem = 'too-young';
      else if (age > 120) birthYearProblem = 'unrealistic';
    }
  }
  const birthYearMessage: Record<BirthYearProblem, string> = {
    'too-young':
      "Looks like you're under 13 — a grown-up needs to set up the account first, then add a profile for you.",
    future: 'That year is in the future — double-check it.',
    unrealistic: "That doesn't look right — double-check the year.",
  };
  // Birth year is required at signup since 5.1 — empty input blocks
  // submit alongside the typed-but-bad cases (under 13, future,
  // unrealistic). Backend enforces the same gate so a hand-crafted
  // POST can't slip through.
  const birthYearMissing = mode === 'signup' && birthYearTrim.length === 0;
  const birthYearBlocksSubmit =
    mode === 'signup' && (birthYearMissing || birthYearProblem !== null);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!trimmedEmail || !passwordValid || submissionPending) return;
    if (birthYearBlocksSubmit) return;
    // `mutate` (not `mutateAsync`) — we want errors surfaced via the
    // mutation's `error` state, not by re-throwing into the event
    // handler (which would become an unhandled rejection). onSuccess
    // closes the dialog; the alert below renders when error fires.
    const onSuccess = (): void => onClose();
    if (mode === 'signin') {
      signin.mutate({ email: trimmedEmail, password }, { onSuccess });
    } else {
      // birthYearBlocksSubmit guarantees we have a parseable number
      // here in signup mode — empty / invalid inputs short-circuit
      // above. Cast to int for the API contract.
      const yearNum = Number.parseInt(birthYearTrim, 10);
      signup.mutate({ email: trimmedEmail, password, birthYear: yearNum }, { onSuccess });
    }
  };

  const handleMagicLink = (): void => {
    if (!trimmedEmail || submissionPending) return;
    requestMagicLink.mutate({ email: trimmedEmail }, { onSuccess: () => setMagicLinkSent(true) });
  };

  const handleResetRequest = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!trimmedEmail || submissionPending) return;
    requestReset.mutate(
      { email: trimmedEmail },
      {
        onSuccess: (resp) => {
          setResetSent(true);
          // The dev pipeline returns the bare token in `link` outside
          // production so e2e and local devs don't need a working
          // mailer. Build the full URL the kid would normally click
          // from their inbox so the studio can be self-tested without
          // hand-crafting query strings.
          if (resp.link && typeof window !== 'undefined') {
            setResetDevLink(`${window.location.origin}/reset-password?token=${resp.link}`);
          } else {
            setResetDevLink(null);
          }
        },
      },
    );
  };

  const error =
    signin.error ?? signup.error ?? requestMagicLink.error ?? requestReset.error ?? null;

  return (
    <div
      className="bl-auth__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bl-auth"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <header className="bl-auth__header">
          <h2 id={titleId} className="bl-auth__title">
            {forgotMode
              ? 'Reset your password'
              : mode === 'signin'
                ? 'Welcome back'
                : "Let's set you up"}
          </h2>
          <button type="button" className="bl-auth__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        {forgotMode ? (
          <form className="bl-auth__form" onSubmit={handleResetRequest}>
            <p className="bl-auth__alt-text">
              Type the email on your account. We&apos;ll send you a link to set a new password.
            </p>
            <label className="bl-auth__field">
              <span className="bl-auth__label">Email</span>
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-autofocus
              />
            </label>
            <div className="bl-auth__actions">
              <Button type="submit" disabled={submissionPending || !trimmedEmail}>
                {submissionPending ? 'Working…' : 'Send reset link'}
              </Button>
            </div>
            {resetSent && (
              <p className="bl-auth__sent" role="status">
                Check your email — if there&apos;s an account on that address, the link is on
                its way.
              </p>
            )}
            {resetDevLink && (
              <p className="bl-auth__sent">
                <span className="bl-auth__hint">Dev shortcut:</span>{' '}
                <a href={resetDevLink}>open reset link</a>
              </p>
            )}
            <div className="bl-auth__alt">
              <button
                type="button"
                className="bl-auth__linklike"
                onClick={() => {
                  setForgotMode(false);
                  setResetSent(false);
                  setResetDevLink(null);
                  requestReset.reset();
                }}
              >
                ← Back to sign in
              </button>
            </div>
            {error && (
              <p role="alert" className="bl-auth__error">
                {error.message}
              </p>
            )}
          </form>
        ) : (
        <>
        <fieldset className="bl-auth__mode" aria-label="Account action">
          <legend className="bl-auth__sr-only">Account action</legend>
          <label
            className={`bl-auth__mode-tab${mode === 'signin' ? ' bl-auth__mode-tab--active' : ''}`}
          >
            <input
              type="radio"
              name="bl-auth-mode"
              checked={mode === 'signin'}
              onChange={() => setMode('signin')}
            />
            Sign in
          </label>
          <label
            className={`bl-auth__mode-tab${mode === 'signup' ? ' bl-auth__mode-tab--active' : ''}`}
          >
            <input
              type="radio"
              name="bl-auth-mode"
              checked={mode === 'signup'}
              onChange={() => setMode('signup')}
            />
            New here
          </label>
        </fieldset>

        <form className="bl-auth__form" onSubmit={handleSubmit}>
          <label className="bl-auth__field">
            <span className="bl-auth__label">Email</span>
            <input
              ref={emailInputRef}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-autofocus
            />
          </label>

          <label className="bl-auth__field">
            <span className="bl-auth__label">
              Password
              {mode === 'signup' && <span className="bl-auth__hint"> (8 or more characters)</span>}
            </span>
            <RevealableInput
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          {mode === 'signup' && (
            <label className="bl-auth__field">
              <span className="bl-auth__label">
                What year were you born?
                <span className="bl-auth__hint">
                  {' '}
                  (so we know if a grown-up should set up the account)
                </span>
              </span>
              <input
                type="number"
                inputMode="numeric"
                required
                min={1900}
                max={new Date().getFullYear()}
                step={1}
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value)}
                placeholder="2010"
                aria-invalid={birthYearProblem !== null || undefined}
                aria-describedby={birthYearProblem !== null ? 'bl-auth-birth-year-msg' : undefined}
              />
              {birthYearProblem !== null && (
                <p
                  id="bl-auth-birth-year-msg"
                  className={
                    birthYearProblem === 'too-young'
                      ? 'bl-auth__inline-notice bl-auth__inline-notice--info'
                      : 'bl-auth__inline-notice bl-auth__inline-notice--warn'
                  }
                  role={birthYearProblem === 'too-young' ? 'status' : undefined}
                >
                  {birthYearMessage[birthYearProblem]}
                </p>
              )}
            </label>
          )}

          <div className="bl-auth__actions">
            <Button
              type="submit"
              disabled={
                submissionPending || !trimmedEmail || !passwordValid || birthYearBlocksSubmit
              }
            >
              {submissionPending ? 'Working…' : mode === 'signin' ? 'Sign in' : 'Create account'}
            </Button>
          </div>

          <div className="bl-auth__alt">
            <p className="bl-auth__alt-text">Or skip the password:</p>
            <Button
              variant="secondary"
              type="button"
              onClick={handleMagicLink}
              disabled={submissionPending || !trimmedEmail}
            >
              Email me a sign-in link
            </Button>
            {magicLinkSent && (
              <p className="bl-auth__sent" role="status">
                Check your email — we sent you a one-tap link.
              </p>
            )}
            {mode === 'signin' && (
              <button
                type="button"
                className="bl-auth__linklike"
                onClick={() => {
                  setForgotMode(true);
                  signin.reset();
                  signup.reset();
                  requestMagicLink.reset();
                }}
              >
                Forgot your password?
              </button>
            )}
          </div>

          {error && (
            <p role="alert" className="bl-auth__error">
              {error.message}
            </p>
          )}
        </form>
        </>
        )}
      </div>
    </div>
  );
}
